import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { onSnapshot } from "firebase/firestore";
import { Button, Header, Icon, Loader, Message, Segment, Table } from "semantic-ui-react";
import { toast } from "react-toastify";
import { openModal } from "../../../../app/common/modals/modalSlice";
import {
  copyComplianceTemplateToDb,
  deleteComplianceTemplateInDb,
  fetchComplianceTemplatesByBrokerageIdFromDb,
} from "../../../../app/firestore/firestoreService";

export default function BrokerageComplianceManagement() {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const brokerageId = currentUserProfile?.brokerageId || "";
  const brokerageName = currentUserProfile?.brokerageName || "";
  const stateProvince = currentUserProfile?.state || "";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brokerageId) return undefined;
    setLoading(true);
    const q = fetchComplianceTemplatesByBrokerageIdFromDb(brokerageId);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTemplates(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching brokerage compliance templates:", error);
        toast.error("Error loading compliance templates");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [brokerageId]);

  const openTemplateModal = (modalProps) =>
    dispatch(openModal({ modalType: "AdminComplianceTemplateModal", modalProps }));

  const handleCreate = () =>
    dispatch(
      openModal({
        modalType: "BrokerageComplianceNewTemplateModal",
        modalProps: {
          brokerageId,
          stateProvince,
          brokerageTemplates: templates,
        },
      })
    );

  const handleEdit = (template) =>
    openTemplateModal({
      existingTemplate: template,
      isEditMode: true,
      lockBrokerageId: true,
      lockStateProvince: true,
    });

  const handleCopy = async (template) => {
    try {
      const isManager = currentUserProfile?.role === "manager";
      await copyComplianceTemplateToDb({
        ...template,
        userId: currentUserProfile?.userId || template.userId || null,
        managerId: isManager
          ? currentUserProfile.userId
          : template.managerId || null,
      });
      toast.success(`Copied "${template.name}"`);
    } catch (error) {
      console.error("Error copying compliance template:", error);
      toast.error(`Error copying template: ${error.message}`);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete compliance template "${template.name}"? This cannot be undone.`)) return;
    try {
      await deleteComplianceTemplateInDb(template.id);
      toast.success(`Deleted "${template.name}"`);
    } catch (error) {
      console.error("Error deleting compliance template:", error);
      toast.error(`Error deleting template: ${error.message}`);
    }
  };

  if (!brokerageId) {
    return (
      <Message warning>
        <Message.Header>Brokerage ID Required</Message.Header>
        <p>
          Set your Brokerage ID on your Profile before customizing compliance
          templates for your brokerage.
        </p>
      </Message>
    );
  }

  return (
    <div>
      <Header color="blue" as="h2">
        Brokerage Compliance
      </Header>
      <p color="grey">
        Customize the compliance checklist your agents (and any Transaction
        Coordinators added to their transactions) will see for{" "}
        {brokerageName || "your brokerage"}.
        <br />
        Each template applies to a specific Agent Represents direction. If no
        brokerage template exists for a transaction, the state-wide default is
        used.
      </p>
      <Button color="blue" size="small" onClick={handleCreate}>
        <Icon name="plus" />
        New Compliance Template
      </Button>
      <Segment clearing>
        {loading ? (
          <Loader active inline="centered">Loading templates...</Loader>
        ) : templates.length === 0 ? (
          <p>
            No compliance templates configured for this brokerage yet. Click
            "New Compliance Template" to create one.
          </p>
        ) : (
          <Table compact striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>State</Table.HeaderCell>
                <Table.HeaderCell>Agent Represents</Table.HeaderCell>
                <Table.HeaderCell>Categories</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {templates.map((template) => (
                <Table.Row key={template.id}>
                  <Table.Cell>{template.name}</Table.Cell>
                  <Table.Cell>{template.stateProvince}</Table.Cell>
                  <Table.Cell>{template.agentRepresents || "—"}</Table.Cell>
                  <Table.Cell>{template.categories?.length || 0}</Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button size="mini" icon="edit" onClick={() => handleEdit(template)} />
                    <Button size="mini" icon="copy" onClick={() => handleCopy(template)} />
                    <Button size="mini" color="red" icon="trash" onClick={() => handleDelete(template)} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>
    </div>
  );
}
